import React, { useMemo, useState } from 'react';
import { Skull, Target, AlertTriangle, TrendingUp, TrendingDown, Coins, X } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList } from 'recharts';
import { getDeathsAnalytics } from '../../lib/analytics';
import { SectionHeader, Card, ChartTooltip, MetricTile, Insight, CHART_COLORS } from './shared';
import PatchAnnotations from './PatchAnnotations';

// V6.4 : Helpers module-level pour la Distribution (évite TDZ en bundle minifié)
function getDistributionBarColor(deaths) {
  if (deaths <= 2) return '#22d3ee'; // cyan = clean
  if (deaths <= 5) return '#fbbf24'; // amber = mid
  return '#fb7185'; // rose = heavy
}

function DistributionTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-zinc-950 border border-cyan-400/40 p-3 text-xs font-mono shadow-xl">
      <div className="text-cyan-400 font-bold mb-1">{d.deathsLabel} mort{d.deaths > 1 ? 's' : ''}</div>
      <div className="text-zinc-300">{d.count} game{d.count > 1 ? 's' : ''} ({d.pct}%)</div>
    </div>
  );
}

export default function DeathsSection({ games }) {
  const data = useMemo(() => getDeathsAnalytics(games), [games]);
  const [selectedDeath, setSelectedDeath] = useState(null);

  // V6.1 : Death Net Value analytics
  const netValueAnalytics = useMemo(() => {
    const allDeaths = [];
    games.forEach(g => {
      (g.deathDetails || []).forEach(d => {
        if (typeof d.netValue === 'number') {
          allDeaths.push({ ...d, gameId: g.id, champion: g.champion, gameResult: g.result, enemyAdc: g.enemyAdc });
        }
      });
    });

    if (allDeaths.length === 0) return null;

    const sorted = [...allDeaths].sort((a, b) => a.netValue - b.netValue);
    const worst = sorted.slice(0, 5); // les 5 plus coûteuses
    const best = sorted.slice(-5).reverse(); // les 5 meilleures (les plus positives)
    const avgNet = Math.round(allDeaths.reduce((s, d) => s + d.netValue, 0) / allDeaths.length);
    const positive = allDeaths.filter(d => d.netValue > 0).length;
    const positiveRatio = Math.round(positive / allDeaths.length * 100);

    return { allDeaths, worst, best, avgNet, positiveRatio, total: allDeaths.length };
  }, [games]);

  if (data.total === 0) {
    return <div className="border border-dashed border-zinc-800 p-8 text-center text-zinc-500 text-sm">Aucune mort catégorisée pour l'instant</div>;
  }

  const pieData = [
    { name: 'Investment', value: data.byType.investment, color: CHART_COLORS.emerald },
    { name: 'Position', value: data.byType.position, color: CHART_COLORS.amber },
    { name: 'Greed', value: data.byType.greed, color: CHART_COLORS.rose }
  ].filter(d => d.value > 0);

  const greedPercent = data.total > 0 ? Math.round(data.byType.greed / data.total * 100) : 0;
  const trendRecent = data.trends.slice(-5);
  const greedTrend = trendRecent.length >= 2 ? trendRecent[trendRecent.length - 1].greed - trendRecent[0].greed : 0;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total Morts" value={data.total} color="white" />
        <MetricTile label="Morts Greed" value={data.byType.greed} subtext={`${greedPercent}% du total`} color={greedPercent <= 15 ? 'cyan' : 'rose'} />
        <MetricTile label="Morts / Défaite" value={data.avgDeathsInLosses} color="rose" />
        <MetricTile label="Morts / Victoire" value={data.avgDeathsInWins} color="cyan" />
      </div>

      {/* V6.4 : Distribution de morts par game (histogramme vertical Datawrapper-style) */}
      {games.length > 0 && (() => {
        const distribution = {};
        for (let i = 0; i <= 8; i++) distribution[i] = 0;
        distribution[9] = 0; // 9+ devient juste "9"
        games.forEach(g => {
          const d = g.deaths || 0;
          if (d >= 9) distribution[9]++;
          else distribution[d]++;
        });
        const distArr = Object.entries(distribution).map(([deaths, count]) => ({
          deaths: parseInt(deaths),
          deathsLabel: parseInt(deaths) === 9 ? '9+' : String(deaths),
          count,
          pct: Math.round(count / games.length * 100)
        }));
        const cleanGames = (distribution[0] + distribution[1] + distribution[2]);
        const cleanPct = Math.round(cleanGames / games.length * 100);
        const heavyGames = (distribution[6] + distribution[7] + distribution[8] + distribution[9]);
        const heavyPct = Math.round(heavyGames / games.length * 100);
        const modeBucket = distArr.reduce((a, b) => b.count > a.count ? b : a);

        // V6.4 : repérer la dernière game pour annotation
        const lastGame = games[games.length - 1];
        const lastGameDeaths = lastGame ? Math.min(lastGame.deaths || 0, 9) : null;
        const lastGameLabel = lastGame
          ? `${lastGame.champion || 'Game'} vs ${lastGame.enemyAdc || '?'} ${new Date(lastGame.timestamp).toLocaleDateString('fr-FR')}`
          : '';

        return (
          <Card>
            <SectionHeader title="Distribution par Game" subtitle={`Régularité de tes morts sur ${games.length} games`} icon={Target} />

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <MetricTile label="Games disciplinées" value={cleanPct} unit="%" subtext={`${cleanGames} games ≤ 2 morts`} color={cleanPct >= 30 ? 'cyan' : 'rose'} />
              <MetricTile label="Games catastrophe" value={heavyPct} unit="%" subtext={`${heavyGames} games ≥ 6 morts`} color={heavyPct <= 20 ? 'cyan' : 'rose'} />
              <MetricTile label="Mode (le + fréquent)" value={modeBucket.deathsLabel} subtext="morts par game" color="white" />
            </div>

            {/* Histogramme vertical type Datawrapper */}
            <div className="bg-zinc-950 -mx-2 md:-mx-4 px-2 md:px-4 py-2">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={distArr} margin={{ top: 30, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis
                    dataKey="deathsLabel"
                    tick={{ fill: '#a1a1aa', fontSize: 12, fontFamily: 'JetBrains Mono' }}
                    axisLine={{ stroke: '#3f3f46' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#71717a', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<DistributionTooltip />} cursor={{ fill: 'rgba(34, 211, 238, 0.05)' }} />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {distArr.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={getDistributionBarColor(entry.deaths)}
                        stroke={lastGameDeaths === entry.deaths ? '#22d3ee' : 'none'}
                        strokeWidth={lastGameDeaths === entry.deaths ? 2 : 0}
                      />
                    ))}
                    <LabelList
                      dataKey="count"
                      position="top"
                      fill="#e4e4e7"
                      fontSize={11}
                      fontFamily="JetBrains Mono"
                      formatter={(v) => v > 0 ? v : ''}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Annotation dernière game */}
            {lastGame && (
              <div className="mt-2 flex items-start gap-2 text-[11px] font-mono">
                <div className="w-3 h-3 mt-0.5 border-2 border-cyan-400 flex-shrink-0" />
                <div>
                  <span className="text-cyan-400 font-bold">Dernière game</span>
                  <span className="text-zinc-400"> · {lastGameLabel} → {lastGame.deaths || 0} mort{(lastGame.deaths || 0) > 1 ? 's' : ''}</span>
                </div>
              </div>
            )}

            <div className="text-[10px] text-zinc-500 font-mono italic mt-3">
              Idéal : pic vers 1-3 morts. Beaucoup de games ≥ 6 morts = signal de tilt ou matchups difficiles non gérés.
            </div>
          </Card>
        );
      })()}


      {/* V6.2 : Patterns dominants */}
      {data.patterns && data.patterns.some(p => p.count > 0) && (
        <Card>
          <SectionHeader
            title="Patterns Dominants"
            subtitle={`${data.deathsWithPatterns} morts sur ${data.total} ont matché un pattern · 1 mort peut matcher plusieurs`}
            icon={AlertTriangle}
          />

          {/* Top 3 résumé en gros */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {data.patterns.slice(0, 3).filter(p => p.count > 0).map((p, i) => (
              <div key={p.id} className="border border-zinc-800 bg-zinc-950 p-4 relative overflow-hidden">
                <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-20 blur-2xl" style={{ background: p.color }} />
                <div className="relative">
                  <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold mb-1">
                    #{i + 1} · {p.label}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black font-mono" style={{ color: p.color }}>{p.count}</span>
                    <span className="text-zinc-500 text-sm font-mono">{p.pct}%</span>
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-1">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Liste complète en bars horizontales */}
          <div className="space-y-1.5">
            {data.patterns.map(p => {
              const maxCount = Math.max(...data.patterns.map(x => x.count), 1);
              const widthPct = (p.count / maxCount) * 100;
              return (
                <div key={p.id} className="grid grid-cols-[140px_1fr_50px_50px] gap-2 items-center text-xs font-mono">
                  <div className="text-zinc-300 truncate" title={p.desc}>{p.label}</div>
                  <div className="bg-zinc-900 h-4 relative overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full transition-all"
                      style={{ width: `${widthPct}%`, background: p.color, opacity: p.count > 0 ? 1 : 0 }}
                    />
                  </div>
                  <div className="text-right font-bold" style={{ color: p.count > 0 ? p.color : '#52525b' }}>{p.count}</div>
                  <div className="text-right text-zinc-500">{p.pct}%</div>
                </div>
              );
            })}
          </div>

          {/* Insight automatique sur le pattern dominant */}
          {data.patterns[0]?.count > 0 && (
            <div className="mt-4 border-l-4 p-3 text-sm" style={{ borderColor: data.patterns[0].color, background: `${data.patterns[0].color}10` }}>
              <strong style={{ color: data.patterns[0].color }}>🎯 Pattern #1 : {data.patterns[0].label}</strong> — {data.patterns[0].count} occurrences ({data.patterns[0].pct}% de tes morts).
              <span className="text-zinc-400 italic"> {data.patterns[0].desc}.</span>
            </div>
          )}

          <div className="text-[10px] text-zinc-500 font-mono italic mt-3">
            Détection automatique basée sur la position et le contexte. Si une catégorie semble fausse, signale-la et on raffinera l'algo.
          </div>
        </Card>
      )}


      {netValueAnalytics && (
        <Card>
          <SectionHeader title="Death Net Value" subtitle={`Coût/bénéfice gold de tes morts · ${netValueAnalytics.total} morts analysées`} icon={Coins} />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <MetricTile
              label="Net Value Moy."
              value={netValueAnalytics.avgNet >= 0 ? `+${netValueAnalytics.avgNet}` : netValueAnalytics.avgNet}
              unit="g"
              subtext="par mort"
              color={netValueAnalytics.avgNet >= 0 ? 'cyan' : 'rose'}
            />
            <MetricTile
              label="Morts Rentables"
              value={netValueAnalytics.positiveRatio}
              unit="%"
              subtext={`${netValueAnalytics.allDeaths.filter(d => d.netValue > 0).length} sur ${netValueAnalytics.total}`}
              color={netValueAnalytics.positiveRatio >= 30 ? 'cyan' : 'amber'}
            />
            <MetricTile
              label="Pire Mort"
              value={netValueAnalytics.worst[0]?.netValue || 0}
              unit="g"
              subtext={`${netValueAnalytics.worst[0]?.champion || ''} ${netValueAnalytics.worst[0]?.minute || 0}min`}
              color="rose"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Top 5 pires morts */}
            <div>
              <div className="text-[10px] tracking-[0.3em] text-rose-400 uppercase font-semibold mb-2 flex items-center gap-2">
                <TrendingDown size={12} /> Top 5 pires morts
              </div>
              <div className="space-y-1">
                {netValueAnalytics.worst.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedDeath(d)}
                    className="w-full text-left border border-zinc-800 hover:border-rose-400/50 bg-zinc-950 px-3 py-2 transition flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-zinc-300 truncate">{d.champion} vs {d.enemyAdc}</div>
                      <div className="text-[10px] text-zinc-500 font-mono truncate">{d.minute}min · {d.type}</div>
                    </div>
                    <div className="text-rose-400 font-mono font-black text-sm">{d.netValue}g</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Top 5 morts rentables */}
            <div>
              <div className="text-[10px] tracking-[0.3em] text-emerald-400 uppercase font-semibold mb-2 flex items-center gap-2">
                <TrendingUp size={12} /> Top 5 morts rentables
              </div>
              <div className="space-y-1">
                {netValueAnalytics.best.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedDeath(d)}
                    className="w-full text-left border border-zinc-800 hover:border-emerald-400/50 bg-zinc-950 px-3 py-2 transition flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-zinc-300 truncate">{d.champion} vs {d.enemyAdc}</div>
                      <div className="text-[10px] text-zinc-500 font-mono truncate">{d.minute}min · {d.type}</div>
                    </div>
                    <div className="text-emerald-400 font-mono font-black text-sm">+{d.netValue}g</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="text-[10px] text-zinc-500 font-mono mt-3 italic">
            Clique sur une mort pour voir le détail (gold perdu, gold gagné, opportunity cost)
          </div>
        </Card>
      )}


      {/* Insights auto */}
      <div className="space-y-2">
        {greedPercent > 25 && (
          <Insight variant="warning">
            <strong>⚠️ Greed élevé</strong> — {greedPercent}% de tes morts sont des morts greed (CS de trop, overstay...). Cible &lt; 15% pour un climb solide.
          </Insight>
        )}
        {greedPercent <= 15 && data.total > 10 && (
          <Insight variant="success">
            <strong>✓ Discipline solide</strong> — Seulement {greedPercent}% de morts greed. Tu maîtrises ton axe #1 du bloc.
          </Insight>
        )}
        {+data.avgDeathsInLosses - +data.avgDeathsInWins > 3 && (
          <Insight variant="info">
            <strong>📊 Pattern détecté</strong> — Tu meurs {(data.avgDeathsInLosses - data.avgDeathsInWins).toFixed(1)}x plus dans tes défaites. Réduire les morts est directement corrélé à gagner.
          </Insight>
        )}
        {greedTrend < -0.2 && (
          <Insight variant="success">
            <strong>📉 En progression</strong> — Ton taux de morts greed diminue sur les 5 dernières fenêtres. Continue.
          </Insight>
        )}
      </div>

      {/* Grid : distribution temporelle + donut type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Histogramme temporel */}
        <Card className="lg:col-span-2">
          <SectionHeader title="Distribution Temporelle" subtitle="À quel moment de la game tu meurs" icon={Target} />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="label" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} tick={{ fill: '#71717a' }} />
              <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} tick={{ fill: '#71717a' }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
              <Bar dataKey="investment" stackId="a" fill={CHART_COLORS.emerald} name="Invest" />
              <Bar dataKey="position" stackId="a" fill={CHART_COLORS.amber} name="Position" />
              <Bar dataKey="greed" stackId="a" fill={CHART_COLORS.rose} name="Greed" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Donut par type */}
        <Card>
          <SectionHeader title="Par Type" subtitle="Répartition globale" icon={Skull} />
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value">
                {pieData.map((entry, i) => <Cell key={i} fill={entry.color} stroke="none" />)}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {pieData.map(d => (
              <div key={d.name} className="flex items-center justify-between text-xs font-mono">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2" style={{ background: d.color }} />
                  <span className="text-zinc-300">{d.name}</span>
                </div>
                <span className="text-zinc-400">{d.value} ({Math.round(d.value / data.total * 100)}%)</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Trend rolling */}
      <Card>
        <SectionHeader title="Évolution Rolling (10 games)" subtitle="Moyenne de morts par game, par type, au fil du bloc" icon={AlertTriangle} />
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.trends}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="gameNumber" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} tick={{ fill: '#71717a' }} />
            <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} tick={{ fill: '#71717a' }} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
            <Line type="monotone" dataKey="greed" stroke={CHART_COLORS.rose} strokeWidth={2} dot={false} name="Greed" />
            <Line type="monotone" dataKey="position" stroke={CHART_COLORS.amber} strokeWidth={2} dot={false} name="Position" />
            <Line type="monotone" dataKey="investment" stroke={CHART_COLORS.emerald} strokeWidth={2} dot={false} name="Invest" />
            <Line type="monotone" dataKey="total" stroke={CHART_COLORS.cyan} strokeWidth={2} strokeDasharray="4 4" dot={false} name="Total" />
          </LineChart>
        </ResponsiveContainer>
        <PatchAnnotations games={games} />
      </Card>

      {/* Top contextes */}
      {data.topContexts.length > 0 && (
        <Card>
          <SectionHeader title="Contextes Récurrents" subtitle="Mots-clés les plus fréquents dans tes morts" />
          <div className="flex flex-wrap gap-2">
            {data.topContexts.map((c, i) => {
              const size = Math.max(0.75, Math.min(1.5, 0.75 + (c.count / data.topContexts[0].count) * 0.75));
              const opacity = Math.max(0.5, c.count / data.topContexts[0].count);
              return (
                <div
                  key={c.word}
                  className="border border-zinc-800 px-3 py-1.5 font-mono text-white"
                  style={{ fontSize: `${size}rem`, opacity }}
                >
                  <span>{c.word}</span>
                  <span className="text-cyan-400 ml-2 text-xs">{c.count}×</span>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-zinc-500 font-mono mt-3">
            Taille proportionnelle à la fréquence. Ces mots-clés révèlent tes patterns récurrents.
          </div>
        </Card>
      )}
      {/* V6.1 : Modal détail d'une mort */}
      {selectedDeath && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 md:p-4" onClick={() => setSelectedDeath(null)}>
          <div className="bg-zinc-950 border border-cyan-400/30 w-full max-w-md my-2 md:my-8" style={{ boxShadow: '0 0 30px rgba(34, 211, 238, 0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-zinc-800 p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-cyan-400 uppercase flex items-center gap-1">
                    <Coins size={10} /> Death Detail
                  </div>
                  <div className="font-black text-lg">
                    <span className="text-white">{selectedDeath.champion}</span>
                    <span className="text-zinc-500 text-sm font-mono ml-2">vs {selectedDeath.enemyAdc} · {selectedDeath.minute}min</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setSelectedDeath(null)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>

            <div className="p-5 space-y-4">
              {/* Net Value en gros */}
              <div className="text-center py-4">
                <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Net Value</div>
                <div className={`font-mono font-black text-5xl ${selectedDeath.netValue >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {selectedDeath.netValue >= 0 ? '+' : ''}{selectedDeath.netValue}
                  <span className="text-2xl ml-1 opacity-70">g</span>
                </div>
                <div className="text-xs text-zinc-500 font-mono mt-2">
                  Type : <span className="text-white">{selectedDeath.type}</span> · Respawn : <span className="text-white">{selectedDeath.respawnSec}s</span>
                </div>
              </div>

              {/* Breakdown coûts */}
              {selectedDeath.costs && (
                <div className="border border-rose-400/30 bg-rose-400/5 p-3">
                  <div className="text-[10px] tracking-[0.3em] text-rose-400 uppercase font-semibold mb-2 flex items-center gap-2">
                    <TrendingDown size={11} /> Coûts ({selectedDeath.costs.total}g)
                  </div>
                  <div className="space-y-1 text-xs font-mono">
                    <div className="flex justify-between"><span className="text-zinc-400">Bounty donné</span><span className="text-rose-400">-{selectedDeath.costs.bountyGiven}g</span></div>
                    <div className="flex justify-between"><span className="text-zinc-400">Gold non-farmé</span><span className="text-rose-400">-{selectedDeath.costs.goldNotFarmed}g</span></div>
                    {selectedDeath.costs.objectivesLost > 0 && (
                      <div className="flex justify-between"><span className="text-zinc-400">Objectifs perdus</span><span className="text-rose-400">-{selectedDeath.costs.objectivesLost}g</span></div>
                    )}
                    {selectedDeath.costs.towersLost > 0 && (
                      <div className="flex justify-between"><span className="text-zinc-400">Towers perdues</span><span className="text-rose-400">-{selectedDeath.costs.towersLost}g</span></div>
                    )}
                    <div className="flex justify-between text-zinc-500 pt-1 mt-1 border-t border-rose-400/20">
                      <span>+ XP perdu</span><span>-{selectedDeath.costs.xpNotGained} XP</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Breakdown gains */}
              {selectedDeath.gains && (selectedDeath.gains.total > 0 ? (
                <div className="border border-emerald-400/30 bg-emerald-400/5 p-3">
                  <div className="text-[10px] tracking-[0.3em] text-emerald-400 uppercase font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp size={11} /> Bénéfices ({selectedDeath.gains.total}g)
                  </div>
                  <div className="space-y-1 text-xs font-mono">
                    {selectedDeath.gains.teamKills > 0 && (
                      <div className="flex justify-between"><span className="text-zinc-400">Kills team pendant respawn</span><span className="text-emerald-400">+{selectedDeath.gains.teamKills}g</span></div>
                    )}
                    {selectedDeath.gains.objectivesGained > 0 && (
                      <div className="flex justify-between"><span className="text-zinc-400">Objectifs pris pendant respawn</span><span className="text-emerald-400">+{selectedDeath.gains.objectivesGained}g</span></div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border border-zinc-800 bg-zinc-900/50 p-3 text-xs text-zinc-500 font-mono italic text-center">
                  Aucun bénéfice — la team n'a rien pu faire pendant ton respawn
                </div>
              ))}

              {/* Context */}
              <div className="text-[11px] text-zinc-500 font-mono italic border-t border-zinc-800 pt-3">
                {selectedDeath.context}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
